import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { QUERY_NAME, SERVICES } from "@lhd/constants";
import axios from "@lhd/libs/axios";
import { CategoryStatus, CategoryStatusCompany } from "@lhd/types";
import { getAccessToken, getCompanyId, getUid } from "@lhd/utility";
import { notification } from "antd";

export const useFetchCategoryStatuses = (
  page: number,
  pageSize: number,
  filterParams: any,
) => {
  const companyId = getCompanyId();
  const uid = getUid();
  return useQuery(
    [
      QUERY_NAME.CATEGORY_STATUSES,
      companyId,
      page,
      pageSize,
      filterParams,
      uid,
    ],
    () =>
      axios({
        method: "get",
        url: SERVICES.CATEGORY_STATUSES,
        params: {
          page,
          per_page: pageSize,
          company_id: companyId,
          ...filterParams,
        },
      }).then((res) => ({
        data: res.data as CategoryStatus[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: 0,
      staleTime: 0,
      enabled: !!companyId,
      refetchOnMount: "always",
      refetchOnReconnect: "always",
      refetchOnWindowFocus: false,
    },
  );
};

export const useFetchOneCategoryStatus = (id: any) => {
  const uid = getUid();
  return useQuery(
    [QUERY_NAME.CATEGORY_STATUS, id, uid],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.CATEGORY_STATUSES}/${id}`,
      }).then((res) => res.data as CategoryStatus),
    {
      cacheTime: 3000,
      staleTime: 3000,
      retry: 2,
      onError({ response }: any) {
        notification["error"]({
          message: response?.data?.error,
        });
      },
      enabled: !!id,
    },
  );
};

export const useSubmitCategoryStatus = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ payload, id }: any) =>
      axios({
        method: id ? "patch" : "post",
        url: id
          ? `${SERVICES.CATEGORY_STATUSES}/${id}`
          : SERVICES.CATEGORY_STATUSES,
        data: { ...payload, company_id: companyId },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.CATEGORY_STATUSES]);
        queryClient.invalidateQueries([QUERY_NAME.CATEGORY_STATUS]);
        queryClient.invalidateQueries([QUERY_NAME.CATEGORY_STATUS_COMPANIES]);
        queryClient.invalidateQueries([QUERY_NAME.CATEGORY_STATUS_COMPANY]);
        notification["success"]({
          message: "更新が完了しました。",
        });
      },
    },
  );
};

export const useRestoreCategoryStatus = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id }: any) =>
      axios({
        method: "post",
        url: `${SERVICES.CATEGORY_STATUSES}/${id}/restore`,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.CATEGORY_STATUS]);
        queryClient.invalidateQueries([QUERY_NAME.CATEGORY_STATUSES]);
        queryClient.invalidateQueries([QUERY_NAME.CATEGORY_STATUS_COMPANIES]);
        queryClient.invalidateQueries([QUERY_NAME.CATEGORY_STATUS_COMPANY]);
        notification["success"]({
          message: "更新が完了しました。",
        });
      },
    },
  );
};

export const useDeleteCategoryStatus = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, is_hard_delete }: any) =>
      axios({
        method: "delete",
        url: `${SERVICES.CATEGORY_STATUSES}/${id}`,
        data: { is_hard_delete },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.CATEGORY_STATUSES]);
        queryClient.invalidateQueries([QUERY_NAME.CATEGORY_STATUS_COMPANIES]);
        queryClient.invalidateQueries([QUERY_NAME.CATEGORY_STATUS_COMPANY]);
        notification["success"]({
          message: "更新が完了しました。",
        });
      },
    },
  );
};

export interface FetchCategoryStatusCompaniesOptions {
  onError?: (error: any) => void;
  showNotificationOnError?: boolean;
  [key: string]: any;
}

// Category Status Company
export const useFetchCategoryStatusCompanies = (
  filterParams: any,
  enabledOverride = true,
  options?: FetchCategoryStatusCompaniesOptions,
) => {
  const companyId = getCompanyId();
  const uid = getUid();
  const accessToken = getAccessToken();
  return useQuery(
    [QUERY_NAME.CATEGORY_STATUS_COMPANIES, companyId, filterParams, uid],
    () =>
      axios({
        method: "get",
        url: SERVICES.CATEGORY_STATUS_COMPANIES,
        params: {
          company_id: companyId,
          per_page: 1000,
          ...filterParams,
        },
      }).then((res) => ({
        data: res.data as CategoryStatusCompany[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: 0,
      staleTime: 0,
      enabled: !!companyId && !!accessToken && enabledOverride,
      refetchOnMount: "always",
      refetchOnReconnect: "always",
      refetchOnWindowFocus: false,
      onError: (error: any) => {
        // Alert user via notification with deduplication key
        if (options?.showNotificationOnError !== false) {
          notification.error({
            key: "CATEGORY_STATUS_COMPANIES_ERROR",
            message:
              error?.response?.data?.error ||
              error?.response?.data?.msg ||
              error?.message ||
              "契約種別・取引状況の取得に失敗しました。",
            description:
              "サーバーから最新のカテゴリ・ステータス情報を取得できませんでした。",
          });
        }

        options?.onError?.(error);
      },
      ...options,
    },
  );
};

export const useFetchOneCategoryStatusCompany = (id: any) => {
  const uid = getUid();
  return useQuery(
    [QUERY_NAME.CATEGORY_STATUS_COMPANY, id, uid],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.CATEGORY_STATUS_COMPANIES}/${id}`,
      }).then((res) => res.data as CategoryStatusCompany),
    {
      cacheTime: 3000,
      staleTime: 3000,
      retry: 2,
      onError({ response }: any) {
        notification["error"]({
          message: response?.data?.error,
        });
      },
      enabled: !!id,
    },
  );
};

export const useSubmitCategoryStatusCompanies = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ payload, id }: any) =>
      axios({
        method: id ? "patch" : "post",
        url: id
          ? `${SERVICES.CATEGORY_STATUS_COMPANIES}/${id}`
          : SERVICES.CATEGORY_STATUS_COMPANIES,
        data: { ...payload, company_id: companyId },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.CATEGORY_STATUS_COMPANIES]);
        queryClient.invalidateQueries([QUERY_NAME.CATEGORY_STATUS_COMPANY]);
        notification["success"]({
          message: "更新が完了しました。",
        });
      },
    },
  );
};

export const useSaveCategoryStatusCompanies = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ payload }: any) =>
      axios({
        method: "post",
        url: `${SERVICES.CATEGORY_STATUS_COMPANIES}/save`,
        data: { ...payload, company_id: companyId },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.CATEGORY_STATUS_COMPANIES]);
        queryClient.invalidateQueries([QUERY_NAME.CATEGORY_STATUS_COMPANY]);
        notification["success"]({
          message: "更新が完了しました。",
        });
      },
    },
  );
};
